<template>
    <div class="container-fluid">
        <!-- LOADING -->
        <Loading :style="{ display: loaded.container && loaded.form ? 'none' : 'block' }"></Loading>
        <!-- LOADED -->
        <div :style="{ display: loaded.container && loaded.form ? 'initial' : 'none' }">
            <!-- TITLE -->
            <div class="mt-2 tabName">
                <h3>
                    {{ $t('edit_web_user') }}: {{ webUserOriginal.firstName }}
                    {{ webUserOriginal.lastName }}
                    {{ webUserOriginal.role ? ` (${$t(`role_${webUserOriginal.role}`)})` : '' }}
                </h3>
            </div>
            <!-- EDIT WEB USER FORM -->
            <div class="row mt-4">
                <div class="col-sm-8 col-md-6 col-lg-5 col-xl-4">
                    <ValidationObserver ref="editWebUserForm" v-slot="{ handleSubmit, invalid }">
                        <form role="form" @submit.prevent="handleSubmit(handleSubmitClick)">
                            <!-- FORM FIELDS -->
                            <WebUserFormFields
                                :edit="true"
                                @loaded="setLoaded($event)"
                                :pharmacies="pharmacies"
                                :webUser="webUser"
                            />
                            <!-- BUTTONS: SUBMIT, CANCEL -->
                            <div class="d-flex flex-row mt-4">
                                <div class="d-flex flex-column mr-3">
                                    <input
                                        type="submit"
                                        class="btn btn-success"
                                        :value="$t('save')"
                                        :disabled="submitClicked || invalid"
                                    />
                                </div>
                                <div class="d-flex flex-column">
                                    <button
                                        type="button"
                                        class="btn btn-md btn-secondary"
                                        v-on:click="returnToPreviousPage"
                                        :disabled="submitClicked"
                                    >
                                        {{ $t('cancel') }}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </ValidationObserver>
                </div>
            </div>
            <!-- MODAL: ARE YOU SURE YOU WANT TO INACTIVATE WEB USER? -->
            <b-modal
                id="submitConfirmationModal"
                centered
                :title="$t('inactivate_own_web_user_are_you_sure_title')"
                hide-footer
            >
                <!-- MODAL CONTENT -->
                <div class="row">
                    <div class="col">
                        <p>{{ $t('inactivate_own_web_user_are_you_sure') }}</p>
                    </div>
                </div>
                <!-- MODAL BUTTONS (OK, CANCEL) -->
                <div class="d-flex row mt-2">
                    <div class="d-flex col justify-content-center">
                        <button class="btn btn-primary mr-4" @click="submit">
                            {{ $t('ok') }}
                        </button>
                        <button class="btn btn-secondary" @click="handleConfirmationCancel">
                            {{ $t('cancel') }}
                        </button>
                    </div>
                </div>
            </b-modal>
        </div>
    </div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import Loading from '@/views/partial/Loading';
import { getPharmacy, getWebUser, handleAxiosError, updateWebUser } from '@/services/axiosService';
import WebUserFormFields from './WebUserFormFields';

export default {
    components: { Loading, WebUserFormFields },
    data() {
        return {
            error: false,
            from: '',
            loaded: {
                container: false,
                form: false
            },
            pharmacies: [],
            submitClicked: false,
            webUser: {
                email: null,
                first_name: null,
                id: null,
                language: null,
                last_name: null,
                password: null,
                password_confirmation: null,
                pharmacy_id: null,
                role: null,
                session_length: null,
                enable_account: null
            },
            webUserOriginal: {}
        };
    },
    // save previous page name for routing
    beforeRouteEnter(_to, from, next) {
        next(vm => {
            vm.from = from.name;
        });
        next();
    },
    async created() {
        // create drugsafe user
        await this.getWebUser();
        if (!this.error && this.webUser.pharmacy_id) {
            // if web user belongs to pharmacy, get pharmacy id
            await this.getPharmacy();
        }
        // set container loaded
        this.setLoaded('container');
    },
    computed: {
        ...mapState('account', ['user'])
    },
    methods: {
        ...mapActions('account', ['logout', 'updateProfile']),
        // get web user's pharmacy (store in array for compatibility)
        async getPharmacy() {
            try {
                this.pharmacies = [await getPharmacy(this.webUser.pharmacy_id)];
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // get web user based on route param
        async getWebUser() {
            try {
                const webUser = await getWebUser(this.$route.params.id);
                this.setWebUser(webUser);
            } catch (error) {
                handleAxiosError(error);
                this.error = true;
            }
        },
        // handle confirmation modal cancel action
        handleConfirmationCancel() {
            this.submitClicked = false;
            this.$bvModal.hide('submitConfirmationModal');
        },
        // handle submit click
        async handleSubmitClick() {
            this.submitClicked = true;
            if (this.user.id === this.webUser.id && this.user.status === 'inactive') {
                // if current user is inactivating themselves, show confirmation modal
                this.$bvModal.show('submitConfirmationModal');
            } else {
                // otherwise submit directly
                await this.submit();
            }
        },
        // return to previous page (if returning to user index, retain previous tab selection)
        returnToPreviousPage() {
            if (this.from === 'user.index') {
                this.$router.push({ name: this.from, params: { tab: 'web' } });
            } else {
                this.$router.go(-1);
            }
        },
        // set component loaded
        setLoaded(component) {
            if (!this.error) {
                this.loaded[component] = true;
            }
        },
        // set user in variable
        setWebUser(user) {
            this.webUserOriginal = {
                firstName: user.first_name,
                lastName: user.last_name,
                role: user.role
            };
            this.webUser = user;
        },
        // submit form and return to previous page
        async submit() {
            try {
                this.$bvModal.hide('submitConfirmationModal');
                await updateWebUser(this.webUser.id, this.webUser);
                this.$toastr.s(this.$t('web_user_updated'));
                // if updated user is current user:
                if (this.user.id === this.webUser.id) {
                    if (this.webUser.status === 'inactive') {
                        // if user has inactivated themselves, log out
                        this.logout();
                        return;
                    } else {
                        // otherwise update user profile
                        await this.updateProfile();
                    }
                }
                this.returnToPreviousPage();
            } catch (error) {
                handleAxiosError(error);
                if (error.response.data.status === 409) {
                    // if backend returns 409 (= e-mail address already taken), set appropriate form field error
                    this.$refs.editWebUserForm.setErrors({ webUserEmail: ['error_email_taken'] });
                }
                this.submitClicked = false;
            }
        }
    },
    // handle other "hide modal" actions (background click, esc key press, modal close click)
    mounted() {
        this.$root.$on('bv::modal::hide', bvEvent => {
            if (['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) {
                this.submitClicked = false;
            }
        });
    }
};
</script>
